import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { featureVariants } from '../styles/animations';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

export const FeatureCard = ({ icon, title, description }: FeatureCardProps) => {
  return (
    <motion.div variants={featureVariants}>
      <Box
        sx={{
          backgroundColor: 'rgba(20, 20, 20, 0.95)',
          padding: { xs: '15px', sm: '30px' },
          borderRadius: '8px',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
          height: { xs: '160px', sm: '250px' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          overflow: 'hidden',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&:hover': {
            transform: 'translateY(-5px)',
            border: 'none',
          },
          // Border animations
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            width: 0,
            height: '1px',
            background: '#cc0000',
            transition: 'width 0.5s ease',
            transform: 'translateX(-50%)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            width: 0,
            height: '1px',
            background: '#cc0000',
            transition: 'width 0.5s ease',
            transform: 'translateX(-50%)',
          },
          '& > span:first-of-type': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            width: '1px',
            height: 0,
            background: '#cc0000',
            transition: 'height 0.5s ease',
            transform: 'translateY(-50%)',
          },
          '& > span:last-of-type': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: '50%',
            width: '1px',
            height: 0,
            background: '#cc0000',
            transition: 'height 0.5s ease',
            transform: 'translateY(-50%)',
          },
          '&:hover::before, &:hover::after': {
            width: '100%',
          },
          '&:hover > span': {
            height: '100%',
          },
          // Reflective effect
          '& > div:first-of-type': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(255,255,255,0.1) 0%, transparent 50%)',
            opacity: 0,
            transition: 'opacity 0.3s',
            pointerEvents: 'none',
            zIndex: 1,
          },
          '&:hover > div:first-of-type': {
            opacity: 1,
          },
        }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          e.currentTarget.style.setProperty('--mouse-x', `${x}%`);
          e.currentTarget.style.setProperty('--mouse-y', `${y}%`);
        }}
      >
        <div></div>
        <span></span>
        <span></span>
        <Box
          sx={{
            color: '#cc0000',
            marginBottom: '15px',
            flex: '0 0 auto',
            position: 'relative',
            zIndex: 2,
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontFamily: 'Rubik Mono One',
            fontSize: 'clamp(0.7rem, 1.2vw, 1.2rem)',
            color: '#ffffff',
            marginBottom: { xs: '5px', sm: '10px' },
            flex: '0 0 auto',
            position: 'relative',
            zIndex: 2,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: '#cccccc',
            fontSize: { xs: '0.8rem', sm: '1rem' },
            flex: '1 1 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 2,
          }}
        >
          {description}
        </Typography>
      </Box>
    </motion.div>
  );
};
